import React, { useState, useEffect, useMemo } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import api from '../api/axios';
import './SalonProfile.css';


import {
  MapPin,
  Phone,
  Clock,
  Calendar,
  CheckCircle2,
  ChevronLeft,
  ChevronRight,
  AlertCircle,
  User,
  Sparkles,
  LogOut,
  ShieldAlert
} from 'lucide-react';

export default function SalonProfile() {
  const { slug } = useParams();
  const navigate = useNavigate();

  // وضعیت‌های عمومی سالن و پرسنل
  const [salon, setSalon] = useState(null);
  const [services, setServices] = useState([]);
  const [staffList, setStaffList] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [staffLoading, setStaffLoading] = useState(false);

  // وضعیت‌های مراحل و فرم رزرو
  const [currentStep, setCurrentStep] = useState(1);
  const [selectedServices, setSelectedServices] = useState([]);
  const [selectedStaff, setSelectedStaff] = useState('any');
  const [selectedDate, setSelectedDate] = useState('');
  const [selectedSlot, setSelectedSlot] = useState(null);
  const [slots, setSlots] = useState([]);
  const [slotsLoading, setSlotsLoading] = useState(false);
  const [bookingSubmitting, setBookingSubmitting] = useState(false);
  const [bookingSuccess, setBookingSuccess] = useState(false);
  const [bookingError, setBookingError] = useState(null);

  // وضعیت‌های کاربر و احراز هویت پیامکی (OTP)
  const [currentUser, setCurrentUser] = useState(null);
  const [showAuthModal, setShowAuthModal] = useState(false);
  const [authPhone, setAuthPhone] = useState('');
  const [authCode, setAuthCode] = useState('');
  const [authStep, setAuthStep] = useState('phone'); // 'phone' | 'otp'
  const [authLoading, setAuthLoading] = useState(false);
  const [authError, setAuthError] = useState(null);




  useEffect(() => {
    if (salon?.name) {
      document.title = `${salon.name} | رزرو نوبت`;
    }
  }, [salon]);




  const formatToJalali = (dateStr) => {
    if (!dateStr) return '-';
    try {
      const date = new Date(`${dateStr}T00:00:00`);
      const formatter = new Intl.DateTimeFormat('fa-IR-u-nu-latn', {
        calendar: 'persian',
        weekday: 'long',
        day: 'numeric',
        month: 'long',
        year: 'numeric',
      });

      const parts = formatter.formatToParts(date);
      const getPart = (type) => parts.find((p) => p.type === type)?.value || '';

      const weekday = getPart('weekday');
      const day = getPart('day');
      const month = getPart('month');
      const year = getPart('year');

      // خروجی دقیق: دوشنبه، ۶ مهر ۱۴۰۵
      return `${weekday}، ${day} ${month} ${year}`;
    } catch (e) {
      return dateStr;
    }
  };





  /*
   * کلید سرویس‌های انتخاب‌شده جهت بهینه‌سازی فراخوانی API
   */
  const serviceIdsKey = useMemo(() => {
    return selectedServices
      .map((service) => service.id)
      .sort((a, b) => Number(a) - Number(b))
      .join(',');
  }, [selectedServices]);

  /*
   * تولید تاریخ‌های هفته جاری
   */
  const availableDates = useMemo(() => {
    const dates = [];
    const today = new Date();

    for (let index = 0; index < 7; index += 1) {
      const date = new Date(today);
      date.setDate(today.getDate() + index);

      const iso = date.toISOString().split('T')[0];
      const label = new Intl.DateTimeFormat('fa-IR', {
        weekday: 'short',
        month: 'short',
        day: 'numeric',
      }).format(date);

      dates.push({ iso, label });
    }

    return dates;
  }, []);

  /*
   * محاسبه مجموع زمان
   */
  const totalDuration = useMemo(() => {
    return selectedServices.reduce((total, service) => {
      return (
        total +
        (Number(service.duration_minutes) ||
          Number(service.duration) ||
          0)
      );
    }, 0);
  }, [selectedServices]);

  /*
   * محاسبه مجموع قیمت
   */
  const totalPrice = useMemo(() => {
    return selectedServices.reduce((total, service) => {
      return total + (Number(service.price) || 0);
    }, 0);
  }, [selectedServices]);

  // بررسی دسترسی مدیریت سالن
  const canManage = currentUser?.salons?.some(
    (s) => s.slug === slug && ['owner', 'manager'].includes(s.role)
  );

  /*
   * بررسی وضعیت لاگین کاربر در ابتدای بارگذاری
   */
  useEffect(() => {
    const fetchUser = async () => {
      try {
        const res = await api.get('/user');
        setCurrentUser(res.data);
      } catch (error) {
        setCurrentUser(null);
      }
    };
    fetchUser();
  }, []);

  /*
   * دریافت اطلاعات اولیه سالن و خدمات
   */
  useEffect(() => {
    const fetchInitialData = async () => {
      try {
        setLoading(true);
        setError(null);

        const [salonResponse, servicesResponse] = await Promise.all([
          api.get(`/salons/${slug}`),
          api.get(`/salons/${slug}/services`),
        ]);

        const salonData = salonResponse.data?.data || salonResponse.data;
        const servicesData =
          servicesResponse.data?.data || servicesResponse.data || [];

        setSalon(salonData);
        setServices(Array.isArray(servicesData) ? servicesData : []);
      } catch (err) {
        console.error('Error fetching salon data:', err);
        setError('خطا در بارگذاری اطلاعات سالن. لطفاً دوباره تلاش کنید.');
      } finally {
        setLoading(false);
      }
    };

    if (slug) {
      fetchInitialData();
    }
  }, [slug]);

  /*
   * دریافت لیست آرایشگران بر اساس سرویس‌های انتخاب‌شده
   */
  useEffect(() => {
    if (!slug) return undefined;

    const controller = new AbortController();

    const fetchStaffByServices = async () => {
      try {
        setStaffLoading(true);

        const params = new URLSearchParams();
        selectedServices.forEach((service) => {
          params.append('service_ids[]', service.id);
        });

        const queryString = params.toString();
        const url = `/salons/${slug}/staff${queryString ? `?${queryString}` : ''}`;

        const response = await api.get(url, {
          signal: controller.signal,
        });

        const staffData = response.data?.data || response.data || [];
        setStaffList(Array.isArray(staffData) ? staffData : []);

        setSelectedStaff((previousStaff) => {
          if (previousStaff && previousStaff !== 'any') {
            const stillAvailable = staffData.some(
              (person) => person.id === previousStaff.id
            );
            return stillAvailable ? previousStaff : 'any';
          }
          return previousStaff;
        });
      } catch (err) {
        if (err.name !== 'CanceledError' && err.name !== 'AbortError') {
          console.error('Error fetching staff list:', err);
          setStaffList([]);
        }
      } finally {
        if (!controller.signal.aborted) {
          setStaffLoading(false);
        }
      }
    };

    fetchStaffByServices();

    return () => {
      controller.abort();
    };
  }, [slug, serviceIdsKey]);

  /*
   * فراخوانی اسلات‌های خالی
   */
  const fetchAvailableSlots = async (date, currentStaff = selectedStaff) => {
    if (!date || !slug) return;

    try {
      setSlotsLoading(true);
      setBookingError(null);
      setSelectedSlot(null);
      setSlots([]);

      const params = new URLSearchParams();
      params.append('date', date);

      if (currentStaff && currentStaff !== 'any') {
        params.append('staff_id', currentStaff.id);
      } else if (selectedServices.length > 0) {
        selectedServices.forEach((service) => {
          params.append('service_ids[]', service.id);
        });
      }

      if (totalDuration > 0) {
        params.append('duration', totalDuration);
      }

      const response = await api.get(`/salons/${slug}/slots?${params.toString()}`);
      const slotsData =
        response.data?.data || response.data?.slots || response.data || [];

      setSlots(Array.isArray(slotsData) ? slotsData : []);
    } catch (err) {
      console.error('Error fetching slots:', err);
      setBookingError('خطا در دریافت زمان‌های خالی. لطفاً تاریخ دیگری را انتخاب کنید.');
      setSlots([]);
    } finally {
      setSlotsLoading(false);
    }
  };

  /*
   * تغییر وضعیت انتخاب سرویس
   */
  const toggleService = (service) => {
    setSelectedServices((previousServices) => {
      const exists = previousServices.some((item) => item.id === service.id);
      if (exists) {
        return previousServices.filter((item) => item.id !== service.id);
      }
      return [...previousServices, service];
    });

    setSelectedSlot(null);
    setSlots([]);
  };

  /*
   * انتخاب آرایشگر
   */
  const handleStaffSelect = (staff) => {
    setSelectedStaff(staff);
    setSelectedSlot(null);
    setSlots([]);

    if (selectedDate) {
      fetchAvailableSlots(selectedDate, staff);
    }
  };

  /*
   * انتخاب تاریخ
   */
  const handleDateSelect = (date) => {
    setSelectedDate(date);
    setSelectedSlot(null);
    fetchAvailableSlots(date);
  };

  /*
   * خروج از حساب
   */
  const handleLogout = async () => {
    try {
      await api.post('/auth/logout');
    } catch (err) {
      console.error('Logout error:', err);
    } finally {
      localStorage.removeItem('token');
      setCurrentUser(null);
      navigate(`/salons/${slug}`, { replace: true });
    }
  };

  /*
   * ارسال کد پیامک (OTP)
   */
  const handleSendOtp = async (e) => {
    if (e) e.preventDefault();
    setAuthError(null);

    if (!/^09\d{9}$/.test(authPhone)) {
      setAuthError('لطفاً یک شماره موبایل معتبر ۱۱ رقمی (مثلاً 09123456789) وارد کنید.');
      return;
    }

    try {
      setAuthLoading(true);
      await api.post('/auth/otp/request', { phone: authPhone });
      setAuthStep('otp');
    } catch (err) {
      setAuthError(err.response?.data?.message || 'خطا در ارسال پیامک. لطفاً دوباره تلاش کنید.');
    } finally {
      setAuthLoading(false);
    }
  };

  /*
   * تأیید کد و ذخیره توکن
   */
  const handleVerifyOtp = async (e) => {
    if (e) e.preventDefault();
    setAuthError(null);

    try {
      setAuthLoading(true);
      const response = await api.post('/auth/otp/verify', {
        phone: authPhone,
        code: authCode,
        salon_slug: slug,
      });

      // const token = response.data?.token || response.data?.access_token;
      // if (token) {
      //   localStorage.setItem('token', token);
      // }

      const token = response.data.data?.token || response.data.data?.access_token;


      if (token) {
          localStorage.setItem('token', token); // مطمئن شوید نام کلید با axios.js یکی است
          api.defaults.headers.common['Authorization'] = `Bearer ${token}`;
      }






      setShowAuthModal(false);
      executeBooking();
    } catch (err) {
      setAuthError(err.response?.data?.message || 'کد وارد شده صحیح نیست یا منقضی شده است.');
    } finally {
      setAuthLoading(false);
    }
  };

  /*
   * ریست کردن مراحل رزرو
   */
  const handleResetBooking = () => {
    setCurrentStep(1);
    setSelectedServices([]);
    setSelectedStaff('any');
    setSelectedDate('');
    setSelectedSlot(null);
    setSlots([]);
    setBookingSuccess(false);
    setBookingError(null);
  };

  /*
   * بدنه اصلی ثبت رزرو پس از لاگین بودن
   */
  const executeBooking = async () => {
    try {
      setBookingSubmitting(true);
      setBookingError(null);

      const timeValue =
        typeof selectedSlot === 'string'
          ? selectedSlot
          : selectedSlot?.time ||
            selectedSlot?.start_at ||
            selectedSlot?.start_time ||
            selectedSlot?.time_label;

      const formattedTime = timeValue
        ? timeValue.includes('T')
          ? timeValue.split('T')[1].substring(0, 5)
          : timeValue.includes(' ')
            ? timeValue.split(' ')[1].substring(0, 5)
            : String(timeValue).substring(0, 5)
        : null;

      const primaryServiceId = selectedServices[0]?.id || null;
      const additionalServiceIds = selectedServices
        .slice(1)
        .map((service) => service.id);

      const payload = {
        salon_slug: slug,
        service_id: primaryServiceId,
        additional_service_ids: additionalServiceIds,
        staff_id:
          (selectedSlot &&
            typeof selectedSlot === 'object' &&
            selectedSlot.staff_id) ||
          (selectedStaff !== 'any' ? selectedStaff?.id : null),
        booking_date: selectedDate,
        booking_time: formattedTime,
      };

      await api.post(`/salons/${slug}/bookings`, payload);
      setBookingSuccess(true);
    } catch (err) {
      console.error('Booking failed:', err);

      if (err.response?.status === 401) {
        localStorage.removeItem('token');
        setShowAuthModal(true);
        return;
      }

      if (err.response?.status === 422 && err.response?.data?.errors) {
        const validationErrors = Object.values(err.response.data.errors)
          .flat()
          .join('\n');
        setBookingError(validationErrors);
      } else {
        setBookingError(
          err.response?.data?.message ||
            'متأسفانه در ثبت نوبت خطایی رخ داد. لطفاً دوباره تلاش کنید.'
        );
      }
    } finally {
      setBookingSubmitting(false);
    }
  };

  /*
   * بررسی لاگین قبل از ثبت نهایی
   */
  const handleBookingSubmit = async () => {
    if (!selectedSlot || selectedServices.length === 0 || !salon) {
      return;
    }

    const token = localStorage.getItem('token');
    if (!token) {
      setShowAuthModal(true);
      return;
    }

    await executeBooking();
  };

  if (loading) {
    return (
      <div className="salon-loading-container">
        <div className="salon-spinner"></div>
        <p>در حال دریافت اطلاعات سالن...</p>
      </div>
    );
  }

  if (error || !salon) {
    return (
      <div className="salon-error-container">
        <AlertCircle size={48} className="salon-error-icon" />
        <h2>خطا در نمایش صفحه</h2>
        <p>{error || 'سالن مورد نظر یافت نشد.'}</p>
        <button
          type="button"
          onClick={() => navigate('/')}
          className="salon-btn-primary"
        >
          بازگشت به صفحه اصلی
        </button>
      </div>
    );
  }

  return (
    <div className="salon-profile-page" dir="rtl">
      {/* هدر سالن */}
      <header
        className="salon-header"
        style={{ backgroundImage: `url("${salon.cover_url}")` }}
      >
        <div className="salon-info-bar">
          <div className="salon-info-container">
            <div className="salon-avatar-wrapper">
              <img
                src={salon.logo_url || '/placeholder-logo.png'}
                alt={salon.name}
                className="salon-avatar-img"
              />
            </div>

            <div className="salon-meta-text">
              <h1 className="salon-title">{salon.name}</h1>

              {salon.description && (
                <p className="salon-description">{salon.description}</p>
              )}

              <div className="salon-badges">
                {salon.address && (
                  <span className="salon-badge">
                    <MapPin size={16} />
                    {salon.address}
                  </span>
                )}

                {salon.phone && (
                  <span className="salon-badge">
                    <Phone size={16} />
                    {salon.phone}
                  </span>
                )}
              </div>
            </div>

            {localStorage.getItem('token') && (
              <button
                onClick={handleLogout}
                className="salon-btn-secondary"
                style={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: '6px',
                  cursor: 'pointer',
                }}
              >
                <LogOut size={16} />
                <span>خروج از حساب</span>
              </button>
            )}

            {canManage && (
              <button
                className="salon-btn-primary mr-auto"
                style={{ margin: '0' }}
                onClick={() => navigate(`/salons/${slug}/admin`)}
              >
                پنل مدیریت سالن
              </button>
            )}
          </div>
        </div>
      </header>

      {/* بدنه و مراحل رزرواسیون */}
      <main className="salon-main-container">
        <div className="booking-stepper">
          <div
            className={`step-item ${currentStep >= 1 ? 'active' : ''} ${
              currentStep > 1 ? 'completed' : ''
            }`}
          >
            <span className="step-num">{currentStep > 1 ? '✓' : '۱'}</span>
            <span className="step-label">انتخاب خدمت</span>
          </div>

          <div className="step-divider"></div>

          <div
            className={`step-item ${currentStep >= 2 ? 'active' : ''} ${
              currentStep > 2 ? 'completed' : ''
            }`}
          >
            <span className="step-num">{currentStep > 2 ? '✓' : '۲'}</span>
            <span className="step-label">انتخاب آرایشگر</span>
          </div>

          <div className="step-divider"></div>

          <div
            className={`step-item ${currentStep >= 3 ? 'active' : ''} ${
              currentStep > 3 ? 'completed' : ''
            }`}
          >
            <span className="step-num">{currentStep > 3 ? '✓' : '۳'}</span>
            <span className="step-label">زمان نوبت</span>
          </div>

          <div className="step-divider"></div>

          <div className={`step-item ${currentStep >= 4 ? 'active' : ''}`}>
            <span className="step-num">۴</span>
            <span className="step-label">تأیید نهایی</span>
          </div>
        </div>

        <div className="booking-card">
          {/* مرحله اول: انتخاب سرویس */}
          {currentStep === 1 && (
            <div className="step-content">
              <h2 className="step-title">خدمات مورد نظر خود را انتخاب کنید</h2>

              {services.length === 0 ? (
                <p className="empty-msg">
                  در حال حاضر هیچ خدمتی برای این سالن تعریف نشده است.
                </p>
              ) : (
                <div className="services-grid">
                  {services.map((service) => {
                    const isSelected = selectedServices.some(
                      (item) => item.id === service.id
                    );

                    return (
                      <div
                        key={service.id}
                        className={`service-card ${isSelected ? 'selected' : ''}`}
                        onClick={() => toggleService(service)}
                      >
                        <div className="service-info">
                          <h3 className="service-name">{service.name}</h3>

                          {service.description && (
                            <p className="service-desc">{service.description}</p>
                          )}

                          <div className="service-details">
                            <span className="service-duration">
                              <Clock size={14} />
                              {service.duration_minutes || service.duration || 0} دقیقه
                            </span>

                            <span className="service-price">
                              {Number(service.price || 0).toLocaleString('fa-IR')} تومان
                            </span>
                          </div>
                        </div>

                        <div className="service-checkbox">
                          {isSelected && (
                            <CheckCircle2 size={20} className="text-primary" />
                          )}
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          )}

          {/* مرحله دوم: انتخاب آرایشگر */}
          {currentStep === 2 && (
            <div className="step-content">
              <h2 className="step-title">آرایشگر مورد نظر خود را انتخاب کنید</h2>

              {staffLoading ? (
                <div className="salon-loading-container py-8">
                  <div className="salon-spinner"></div>
                  <p>در حال بررسی آرایشگران مجاز برای خدمات انتخابی...</p>
                </div>
              ) : staffList.length === 0 ? (
                <div className="empty-msg text-center py-6">
                  <AlertCircle className="mx-auto mb-2 text-yellow-500" size={32} />
                  <p>هیچ آرایشگری برای تمامی خدمات انتخابی شما در این سالن یافت نشد.</p>
                </div>
              ) : (
                <div className="staff-grid">
                  <div
                    className={`staff-card ${selectedStaff === 'any' ? 'selected' : ''}`}
                    onClick={() => handleStaffSelect('any')}
                  >
                    <div className="staff-avatar placeholder">
                      <Sparkles size={24} />
                    </div>

                    <div className="staff-details">
                      <span className="staff-name">اولین آرایشگر در دسترس</span>
                      <span className="staff-role">سریع‌ترین زمان ممکن</span>
                    </div>
                  </div>

                  {staffList.map((person) => {
                    const isSelected =
                      selectedStaff !== 'any' && selectedStaff?.id === person.id;

                    return (
                      <div
                        key={person.id}
                        className={`staff-card ${isSelected ? 'selected' : ''}`}
                        onClick={() => handleStaffSelect(person)}
                      >
                        <div className="staff-avatar">
                          {person.avatar ? (
                            <img
                              src={person.avatar}
                              alt={person.name}
                              className="staff-avatar-image"
                            />
                          ) : (
                            <User size={24} />
                          )}
                        </div>

                        <div className="staff-details">
                          <span className="staff-name">{person.name}</span>
                          <span className="staff-role">
                            {person.role || 'آرایشگر سالن'}
                          </span>
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          )}

          {/* مرحله سوم: انتخاب تاریخ و زمان */}
          {currentStep === 3 && (
            <div className="step-content">
              <h2 className="step-title">روز و ساعت حضور را مشخص کنید</h2>

              <div className="date-picker-carousel">
                {availableDates.map((item) => (
                  <button
                    key={item.iso}
                    type="button"
                    className={`date-chip ${
                      selectedDate === item.iso ? 'active' : ''
                    }`}
                    onClick={() => handleDateSelect(item.iso)}
                  >
                    <Calendar size={14} />
                    <span>{item.label}</span>
                  </button>
                ))}
              </div>

              {selectedDate && (
                <div className="slots-section">
                  <h3 className="slots-title">ساعت‌های قابل رزرو:</h3>

                  {slotsLoading ? (
                    <div className="slots-loading">
                      <div className="salon-spinner-sm"></div>
                      <span>در حال دریافت ساعت‌های آزاد...</span>
                    </div>
                  ) : slots.length === 0 ? (
                    <p className="empty-msg">برای این تاریخ هیچ ساعت آزادی یافت نشد.</p>
                  ) : (
                    <div className="slots-grid">
                      {slots.map((slot, index) => {
                        const isObject = typeof slot === 'object' && slot !== null;

                        const slotTime = isObject
                          ? slot.time ||
                            slot.start_time ||
                            slot.time_label ||
                            (slot.start_at ? slot.start_at.substring(11, 16) : '') ||
                            (slot.start ? String(slot.start).substring(11, 16) : '') ||
                            ''
                          : String(slot);

                        const isAvailable = isObject
                          ? slot.is_available !== undefined
                            ? Boolean(slot.is_available)
                            : true
                          : true;
                        const isBooked = isObject ? Boolean(slot.is_booked) : false;
                        const isPast = isObject ? Boolean(slot.is_past) : false;
                        const isDisabled = !isAvailable;

                        const slotKey = isObject
                          ? slot.id || slot.start_at || slot.start_time || slot.time || index
                          : slot;

                        const selectedSlotKey =
                          selectedSlot &&
                          (typeof selectedSlot === 'object'
                            ? selectedSlot.id ||
                              selectedSlot.start_at ||
                              selectedSlot.start_time ||
                              selectedSlot.time
                            : selectedSlot);

                        const isSelected = selectedSlot && selectedSlotKey === slotKey;

                        let statusText = '';
                        if (isBooked) statusText = ' (رزرو شده)';
                        else if (isPast) statusText = ' (گذشته)';

                        return (
                          <button
                            key={slotKey}
                            type="button"
                            disabled={isDisabled}
                            className={`slot-chip ${isSelected ? 'selected' : ''} ${
                              isDisabled ? 'disabled' : ''
                            } ${isBooked ? 'booked' : ''}`}
                            onClick={() => {
                              if (!isDisabled) {
                                setSelectedSlot(slot);
                              }
                            }}
                            title={
                              isDisabled
                                ? isBooked
                                  ? 'این ساعت قبلاً رزرو شده است'
                                  : 'این زمان در دسترس نیست'
                                : ''
                            }
                          >
                            <span>{slotTime || '--:--'}</span>
                            {statusText && (
                              <span className="slot-badge">{statusText}</span>
                            )}
                          </button>
                        );
                      })}
                    </div>
                  )}
                </div>
              )}
            </div>
          )}

          {/* مرحله چهارم: بازبینی و تایید نهایی */}
          {currentStep === 4 && (
            <div className="step-content">
              {bookingSuccess ? (
                <div className="success-banner">
                  <CheckCircle2 size={56} className="text-green-500" />
                  <h2>رزرو شما با موفقیت ثبت شد!</h2>
                  <p>پیامک حاوی جزئیات نوبت برای شما ارسال خواهد شد.</p>

                  <button
                    type="button"
                    onClick={handleResetBooking}
                    className="salon-btn-primary"
                  >
                    ثبت نوبت جدید (مرحله اول)
                  </button>

                  <button
                    type="button"
                    onClick={() => navigate(`/`)}
                    className="salon-btn-primary mt-4"
                  >
                    بازگشت به صفحه اصلی
                  </button>
                </div>
              ) : (
                <>
                  <h2 className="step-title">بررسی و تأیید نهایی رزرو</h2>

                  <div className="booking-summary">
                    <div className="summary-row">
                      <span>سالن:</span>
                      <strong>{salon.name}</strong>
                    </div>

                    <div className="summary-row">
                      <span>خدمات انتخاب‌شده:</span>
                      <strong>{selectedServices.length} خدمت</strong>
                    </div>

                    <div className="summary-row">
                      <span>آرایشگر:</span>
                      <strong>
                        {selectedStaff === 'any'
                          ? 'اولین آرایشگر در دسترس'
                          : selectedStaff?.name || '-'}
                      </strong>
                    </div>

                    <div className="summary-row">
                      <span>تاریخ:</span>
                      <strong>{formatToJalali(selectedDate)}</strong>
                    </div>

                    <div className="summary-row">
                      <span>ساعت:</span>
                      <strong>
                        {typeof selectedSlot === 'string'
                          ? selectedSlot
                          : selectedSlot?.time ||
                            selectedSlot?.time_label ||
                            selectedSlot?.start_at?.substring(11, 16) ||
                            '-'}
                      </strong>
                    </div>

                    <div className="summary-row">
                      <span>مدت خدمات:</span>
                      <strong>{totalDuration} دقیقه</strong>
                    </div>

                    <div className="summary-row total-row">
                      <span>مبلغ نهایی:</span>
                      <strong>
                        {Number(totalPrice).toLocaleString('fa-IR')} تومان
                      </strong>
                    </div>
                  </div>

                  {bookingError && (
                    <div className="booking-error">
                      <AlertCircle size={18} />
                      <span>{bookingError}</span>
                    </div>
                  )}
                </>
              )}
            </div>
          )}

          {/* مدال لاگین و کد تایید OTP */}
          {showAuthModal && (
            <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-4">
              <div className="bg-white rounded-2xl p-6 w-full max-w-sm shadow-xl relative text-right">
                <h3 className="text-lg font-bold text-gray-800 mb-2">
                  {authStep === 'phone' ? 'ورود جهت ثبت نوبت' : 'تأیید شماره موبایل'}
                </h3>
                <p className="text-sm text-gray-500 mb-4">
                  {authStep === 'phone'
                    ? 'برای ثبت رزرو و جلوگیری از نوبت‌های تکراری، شماره موبایل خود را وارد کنید.'
                    : `کد ۶ رقمی ارسال‌شده به شماره ${authPhone} را وارد کنید.`}
                </p>

                {authError && (
                  <div className="mb-4 p-2.5 rounded-lg bg-red-50 text-red-600 text-xs leading-relaxed">
                    {authError}
                  </div>
                )}

                {authStep === 'phone' ? (
                  <form onSubmit={handleSendOtp} className="auth-form">
                    <input
                      type="tel"
                      maxLength={11}
                      dir="ltr"
                      placeholder="09123456789"
                      value={authPhone}
                      onChange={(e) => setAuthPhone(e.target.value)}
                      className="input-group tel-style"
                      disabled={authLoading}
                      autoFocus
                    />
                    <div className="flex gap-2" style={{ display: 'flex', gap: '5px' }}>
                      <button
                        type="submit"
                        disabled={authLoading || !authPhone}
                        className="salon-btn-primary mr-auto"
                      >
                        {authLoading ? 'در حال ارسال...' : 'دریافت کد تأیید'}
                      </button>
                      <button
                        type="button"
                        onClick={() => setShowAuthModal(false)}
                        className="salon-btn-secondary"
                      >
                        انصراف
                      </button>
                    </div>
                  </form>
                ) : (
                  <form onSubmit={handleVerifyOtp} className="space-y-4">
                    <input
                      type="text"
                      maxLength={6}
                      dir="ltr"
                      placeholder="------"
                      value={authCode}
                      onChange={(e) => setAuthCode(e.target.value)}
                      className="input-group tel-style"
                      disabled={authLoading}
                      autoFocus
                    />
                    <div className="flex gap-2" style={{ display: 'flex', gap: '5px' }}>
                      <button
                        type="submit"
                        disabled={authLoading || authCode.length !== 6}
                        className="salon-btn-primary mr-auto"
                      >
                        {authLoading ? 'در حال بررسی...' : 'تأیید و ثبت نوبت'}
                      </button>
                      <button
                        type="button"
                        onClick={() => setAuthStep('phone')}
                        className="salon-btn-secondary"
                      >
                        ویرایش شماره
                      </button>
                    </div>
                  </form>
                )}
              </div>
            </div>
          )}

          {/* دکمه‌های کنترل مراحل */}
          {!bookingSuccess && (
            <div className="step-actions">
              {currentStep > 1 && (
                <button
                  type="button"
                  onClick={() => setCurrentStep((previousStep) => previousStep - 1)}
                  className="salon-btn-secondary"
                  disabled={bookingSubmitting}
                >
                  <ChevronRight size={18} />
                  مرحله قبل
                </button>
              )}

              {currentStep < 4 ? (
                <button
                  type="button"
                  onClick={() => {
                    if (currentStep === 1 && selectedServices.length === 0) return;
                    if (currentStep === 2 && !selectedStaff) return;
                    if (currentStep === 3 && !selectedSlot) return;

                    setCurrentStep((previousStep) => previousStep + 1);
                  }}
                  className="salon-btn-primary mr-auto"
                  disabled={
                    (currentStep === 1 && selectedServices.length === 0) ||
                    (currentStep === 2 && !selectedStaff) ||
                    (currentStep === 3 && !selectedSlot)
                  }
                >
                  مرحله بعد
                  <ChevronLeft size={18} />
                </button>
              ) : (
                <button
                  type="button"
                  onClick={handleBookingSubmit}
                  className="salon-btn-primary mr-auto"
                  disabled={
                    bookingSubmitting ||
                    !selectedSlot ||
                    selectedServices.length === 0
                  }
                >
                  {bookingSubmitting
                    ? 'در حال ثبت...'
                    : 'تأیید و نهایی کردن رزرو'}
                </button>
              )}
            </div>
          )}
        </div>
      </main>
    </div>
  );
}
